import * as React from 'react';
import Layout from "../../Components/layouts";
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { ReportServices } from '../../Services/ReportServices';
import CustomTabs from '../../Components/CustomTabs';
import { RouteSkeleton } from '../../Components/RouteTransition';
import { pageEyebrowStyles, pageTitleTextStyles, surfaceCardStyles } from '../../theme/commonStyles';
import { analyticsColumns, overViewColumn, progressColumn, USER_ROLES } from '../../utils/constants'
import DetailsAnalyticsTab from './component/DetailsAnalyticsTab';
import { useAuthDispatch, useAuthState } from '../../Context';
import useRoutedTab from '../../hooks/useRoutedTab';
import { notify } from 'utils';
import "jspdf-autotable";
import jsPDF from 'jspdf';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx/xlsx.mjs';
import { buildReportPayload, formatAppliedPeriod } from './learningServiceDateRange';
import {
    buildDownloadFields,
    buildProjectOptions,
    collectDownloadRows,
    formatCompanyLabel,
    formatEmailSentAt,
    formatProjectLabel,
    formatTalentAccessEmail,
    mergeFilterOptions,
    preferredCustomerSelection,
    pruneProjectSelection,
    withAttributionColumns,
} from './learningReportFields';
import {
    DEFAULT_PAGE_SIZE,
    emptyPagination,
    mergeReportFilterMeta,
    normalizeFilterMeta,
    outOfRangePage,
    resolvePagination,
} from './learningReportPagination';

const reportServices = new ReportServices();

const TAB_KEYS = ['analytics', 'overview', 'progress'];
const TAB_ROUTE_SLUGS = ['analytic', 'overview', 'progress'];
const REPORT_TYPE_BY_TAB = {
    analytics: 'detailedanalytics',
    overview: 'detailedoverview',
    progress: 'progressreport',
};
const ENDPOINT_BY_TAB = {
    analytics: 'main/detailedanalytics',
    overview: 'main/detailedoverview',
    progress: 'main/progressreport',
};
const DOWNLOAD_ENDPOINT_BY_TAB = {
    analytics: 'download_analytics_report',
    overview: 'download_overview_report',
    progress: 'download_progress_report',
};

const emptySharedFilters = () => ({
    certificate_status: [],
    course: [],
    customer: [],
    project: [],
    email: "",
    date_range: 'all_time',
    date_from: '',
    date_to: '',
});

const perTab = (makeValue) => TAB_KEYS.reduce((acc, key) => ({ ...acc, [key]: makeValue() }), {});

const useStyles = makeStyles((theme) => ({
    page: {
        width: '100%',
        padding: 0,
    },
    pageHeader: {
        marginBottom: theme.spacing(2.5),
    },
    pageEyebrow: {
        ...pageEyebrowStyles,
        marginBottom: theme.spacing(0.5),
    },
    pageTitle: {
        ...pageTitleTextStyles,
        marginBottom: theme.spacing(0.5),
    },
    pageDescription: {
        color: '#647184',
        fontSize: '0.875rem',
        lineHeight: 1.55,
        maxWidth: 640,
    },
    panel: {
        ...surfaceCardStyles,
        padding: theme.spacing(2, 2.25),
        boxSizing: 'border-box',
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(1.5),
        },
    },
}));

const Report = () => {

    const classes = useStyles();
    const [activeIndex, selectTab] = useRoutedTab(TAB_ROUTE_SLUGS, 'type');
    const [initialLoading, setInitialLoading] = React.useState(true);
    const dispatch = useAuthDispatch();
    const { userDetails } = useAuthState(dispatch);
    const [sharedFilters, setSharedFilters] = React.useState(emptySharedFilters);
    const [paginationByTab, setPaginationByTab] = React.useState(() => perTab(() => emptyPagination(DEFAULT_PAGE_SIZE)));
    const [pendingPageByTab, setPendingPageByTab] = React.useState(() => perTab(() => null));
    const [loadingByTab, setLoadingByTab] = React.useState({
        analytics: false,
        overview: false,
        progress: false,
    });
    const [errorByTab, setErrorByTab] = React.useState({
        analytics: '',
        overview: '',
        progress: '',
    });
    const [projectsReportData, setProjectsReportData] = React.useState(() => []);
    const [filters, setFilters] = React.useState(null);
    const [overViewProjectsReportData, setOverViewProjectsReportData] = React.useState(() => []);
    const [overViewFilters, setOverViewFilters] = React.useState(null);
    const [progressProjectsReportData, setProgressProjectsReportData] = React.useState(() => []);
    const [progressFilters, setProgressFilters] = React.useState(null);
    const [downloadReportLoading, setDownloadReportLoading] = React.useState({
        analytics: false,
        progress: false,
        overview: false
    });
    const [printReportLoading, setPrintReportLoading] = React.useState({
        analytics: false,
        progress: false,
        overview: false
    });
    const requestIds = React.useRef({ analytics: 0, overview: 0, progress: 0 });
    const sharedFiltersRef = React.useRef(sharedFilters);
    // Filters behind the rows currently on screen; page navigation must reuse these,
    // not unapplied edits (period changes wait for "Apply filters").
    const appliedFiltersRef = React.useRef(perTab(() => null));
    const lastRequestRef = React.useRef(perTab(() => null));

    React.useEffect(() => {
        sharedFiltersRef.current = sharedFilters;
    }, [sharedFilters]);

    const tabKeyFromIndex = (index) => TAB_KEYS[index] || 'analytics';

    const filterMetaForTab = (tabKey) => {
        if (tabKey === 'analytics') return filters;
        if (tabKey === 'overview') return overViewFilters;
        return progressFilters;
    };

    const getFilterStateForTab = (tabKey) => ({
        ...sharedFiltersRef.current,
        pageno: paginationByTab[tabKey].pageno,
        perpage: paginationByTab[tabKey].perpage,
        report_type: REPORT_TYPE_BY_TAB[tabKey],
    });

    const rememberFilterOptions = (tabKey, filterMeta) => {
        const merge = (prev) => mergeFilterOptions(prev, filterMeta);
        if (tabKey === 'analytics') setFilters(merge);
        else if (tabKey === 'overview') setOverViewFilters(merge);
        else setProgressFilters(merge);
    };

    const applySuccessResult = (tabKey, result, includeFilters, pagination) => {
        const merge = (prev) => mergeReportFilterMeta(prev, result.data?.filter, includeFilters);
        const rows = result.data?.result || [];
        if (tabKey === 'analytics') {
            setFilters(merge);
            setProjectsReportData(rows);
        } else if (tabKey === 'overview') {
            setOverViewFilters(merge);
            setOverViewProjectsReportData(rows);
        } else {
            setProgressFilters(merge);
            setProgressProjectsReportData(rows);
        }
        setPaginationByTab((prev) => ({ ...prev, [tabKey]: pagination }));
        setErrorByTab((prev) => ({ ...prev, [tabKey]: '' }));
    };

    const fetchTabReport = async (tabKey, options = {}) => {
        const {
            filterOverride,
            pageOverride = 0,
            perpageOverride,
            includeFilters = true,
            skipCustomerBootstrap = false,
            allowOutOfRangeRedirect = true,
        } = options;
        const requestFilters = filterOverride || sharedFiltersRef.current;
        const pageno = pageOverride;
        const perpage = perpageOverride || paginationByTab[tabKey].perpage || DEFAULT_PAGE_SIZE;
        const requestId = ++requestIds.current[tabKey];
        lastRequestRef.current[tabKey] = {
            ...options,
            filterOverride: requestFilters,
            pageOverride: pageno,
            perpageOverride: perpage,
            includeFilters,
        };
        const payload = buildReportPayload(
            requestFilters,
            REPORT_TYPE_BY_TAB[tabKey],
            pageno,
            perpage,
            includeFilters
        );

        setLoadingByTab((prev) => ({ ...prev, [tabKey]: true }));
        setPendingPageByTab((prev) => ({ ...prev, [tabKey]: { pageno, perpage } }));

        try {
            const result = await reportServices.getProjectReportData(ENDPOINT_BY_TAB[tabKey], payload);
            if (requestId !== requestIds.current[tabKey]) {
                return;
            }

            if (result && result.status) {
                const filterMeta = normalizeFilterMeta(result.data?.filter);
                // Only auto-select a customer for non-superadmin roles once role is known.
                // If role is still undefined on first paint, `role !== SUPER_ADMIN` would be true
                // and incorrectly select the first customer for a superadmin.
                const role = userDetails?.role;
                const bootstrapCustomerId = preferredCustomerSelection(filterMeta);
                const shouldBootstrapCustomer = includeFilters
                    && !skipCustomerBootstrap
                    && !!role
                    && role !== USER_ROLES.SUPER_ADMIN
                    && !requestFilters.customer?.length
                    && bootstrapCustomerId != null;

                if (shouldBootstrapCustomer) {
                    rememberFilterOptions(tabKey, filterMeta);
                    const nextShared = {
                        ...sharedFiltersRef.current,
                        ...(filterOverride || {}),
                        customer: [bootstrapCustomerId],
                    };
                    setSharedFilters(nextShared);
                    sharedFiltersRef.current = nextShared;
                    await fetchTabReport(tabKey, {
                        filterOverride: nextShared,
                        pageOverride: 0,
                        perpageOverride: perpage,
                        skipCustomerBootstrap: true,
                    });
                    return;
                }

                const pagination = resolvePagination(result.data, { pageno, perpage });
                const lastPage = allowOutOfRangeRedirect
                    ? outOfRangePage(pagination, (result.data?.result || []).length)
                    : null;
                if (lastPage != null) {
                    await fetchTabReport(tabKey, {
                        filterOverride: requestFilters,
                        pageOverride: lastPage,
                        perpageOverride: perpage,
                        includeFilters,
                        skipCustomerBootstrap: true,
                        allowOutOfRangeRedirect: false,
                    });
                    return;
                }

                appliedFiltersRef.current[tabKey] = requestFilters;
                applySuccessResult(tabKey, result, includeFilters, pagination);
            } else {
                setErrorByTab((prev) => ({
                    ...prev,
                    [tabKey]: result?.message || 'Unable to load report',
                }));
            }
        } catch (err) {
            if (requestId === requestIds.current[tabKey]) {
                setErrorByTab((prev) => ({ ...prev, [tabKey]: 'Unable to load report' }));
            }
        } finally {
            if (requestId === requestIds.current[tabKey]) {
                setLoadingByTab((prev) => ({ ...prev, [tabKey]: false }));
                setPendingPageByTab((prev) => ({ ...prev, [tabKey]: null }));
            }
        }
    };

    const fetchTabPage = (tabKey, pageIndex, perpage) => {
        fetchTabReport(tabKey, {
            filterOverride: appliedFiltersRef.current[tabKey] || sharedFiltersRef.current,
            pageOverride: Math.max(0, pageIndex),
            perpageOverride: perpage,
            includeFilters: !filterMetaForTab(tabKey),
            skipCustomerBootstrap: true,
        });
    };

    const handleRetry = (tabKey) => {
        const last = lastRequestRef.current[tabKey];
        fetchTabReport(tabKey, last || { pageOverride: 0 });
    };

    // Only the routed tab is requested; revisiting a tab keeps its rows unless the
    // shared filters changed since its last request (or that request failed).
    React.useEffect(() => {
        let active = true;
        const tabKey = tabKeyFromIndex(activeIndex);
        const lastRequest = lastRequestRef.current[tabKey];
        if (lastRequest && lastRequest.filterOverride === sharedFiltersRef.current && !errorByTab[tabKey]) {
            setInitialLoading(false);
            return undefined;
        }
        const loadInitialReport = async () => {
            await fetchTabReport(tabKey, {
                filterOverride: sharedFiltersRef.current,
                pageOverride: 0,
            });
            if (active) setInitialLoading(false);
        };
        loadInitialReport();
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeIndex]);

    const handleItemClick = () => { }

    const handlePageChange = (evt, page, tabKey = 'analytics') => {
        fetchTabPage(tabKey, page - 1);
    }

    const handleGoToPage = (page, type) => {
        const tabKey = type || 'analytics';
        const totalPages = paginationByTab[tabKey].total_pages;
        const requested = parseInt(page, 10);
        if (!requested || requested < 1 || (totalPages && requested > totalPages)) {
            return;
        }
        fetchTabPage(tabKey, requested - 1);
    }

    const handlePageSizeChange = (perpage, type) => {
        const tabKey = type || 'analytics';
        fetchTabPage(tabKey, 0, perpage);
    }

    const handleOverViewPageChange = (evt, page) => {
        handlePageChange(evt, page, 'overview');
    }

    const handleProgressPageChange = (evt, page) => {
        handlePageChange(evt, page, 'progress');
    }

    const handleChange = (val, field, filterType) => {
        const isPeriodField = field === 'date_range' || field === 'date_from' || field === 'date_to';
        let nextShared = { ...sharedFiltersRef.current, [field]: val };
        if (field === 'customer') {
            const meta = filterType === 'overview'
                ? overViewFilters
                : filterType === 'progress'
                    ? progressFilters
                    : filters;
            nextShared = {
                ...nextShared,
                project: pruneProjectSelection(nextShared.project, buildProjectOptions(meta), val),
            };
        }

        if (field === 'date_range') {
            if (val !== 'custom') {
                nextShared = { ...nextShared, date_from: '', date_to: '' };
            }
        }

        setSharedFilters(nextShared);
        sharedFiltersRef.current = nextShared;

        // Period / custom dates only update local state; Apply filters sends the request.
        if (isPeriodField) {
            setErrorByTab((prev) => ({ ...prev, [filterType]: '' }));
            return;
        }

        fetchTabReport(filterType, {
            filterOverride: nextShared,
            pageOverride: 0,
        });
    }

    const handleSubmitFilter = (type) => {
        const range = sharedFiltersRef.current.date_range || 'all_time';
        if (range === 'custom') {
            const from = sharedFiltersRef.current.date_from || '';
            const to = sharedFiltersRef.current.date_to || '';
            if (!from || !to || from > to) {
                return;
            }
        }
        fetchTabReport(type, {
            filterOverride: sharedFiltersRef.current,
            pageOverride: 0,
        });
    }

    const handleClearFilters = (type) => {
        const cleared = emptySharedFilters();
        setSharedFilters(cleared);
        sharedFiltersRef.current = cleared;
        setErrorByTab({ analytics: '', overview: '', progress: '' });
        fetchTabReport(type || tabKeyFromIndex(activeIndex), {
            filterOverride: cleared,
            pageOverride: 0,
            skipCustomerBootstrap: userDetails?.role === USER_ROLES.SUPER_ADMIN,
        });
    }

    const handleTabChange = (_evt, newIndex) => {
        selectTab(newIndex);
    };

    const handleDownload = async (type) => {
        let columns = [...analyticsColumns];
        let reportType = DOWNLOAD_ENDPOINT_BY_TAB[type] || DOWNLOAD_ENDPOINT_BY_TAB.analytics;

        if (type === 'progress') {
            columns = [...progressColumn];
        } else if (type === 'overview') {
            columns = [...overViewColumn];
        }

        const exportFields = buildDownloadFields(columns);

        if (!sharedFiltersRef.current?.customer?.length) {
            notify('error', "Please select a customer!");
            return null;
        }

        setDownloadReportLoading({ ...downloadReportLoading, [type]: true });
        const filename = reportType;
        const perpage = type === 'analytics' ? 999 : 999999;
        const filterData = buildReportPayload(
            sharedFiltersRef.current,
            reportType,
            0,
            perpage
        );

        const result = await reportServices.getProjectReportData(`main/${reportType}`, filterData);
        try {
            if (result && result.status) {
                if (result?.data && result?.data?.result) {
                    const data = result.data.result;
                    const wb = XLSX.utils.book_new();
                    const combinedData = [
                        exportFields.map((field) => field.label),
                        ...collectDownloadRows(data, exportFields),
                    ];
                    const newSheet = XLSX.utils.aoa_to_sheet(combinedData);
                    XLSX.utils.book_append_sheet(wb, newSheet, 'Combined_Report');
                    const wbout = XLSX.write(wb, { type: "array", bookType: 'xlsx' });
                    const blob = new Blob([wbout], { type: "application/octet-stream" });
                    saveAs(blob, `${filename}.xlsx`);
                } else {
                    notify('error', "Report data not found!");
                }
            } else if (result?.message) {
                notify('error', result.message);
            }

            setDownloadReportLoading({ ...downloadReportLoading, [type]: false });
        } catch (e) {
            console.log("Error:", e);
            setDownloadReportLoading({ ...downloadReportLoading, [type]: false });
        }
    };


    const handlePrint = async (type) => {
        if (!sharedFiltersRef.current?.customer?.length) {
            notify('error', "Please select customer!");
            return null;
        }
        setPrintReportLoading({ ...printReportLoading, [type]: true });
        try {
            const filename = DOWNLOAD_ENDPOINT_BY_TAB[type] || DOWNLOAD_ENDPOINT_BY_TAB.analytics;
            let columns = [
                "Username",
                "Learner Email",
                "Order Type",
                "Course Name",
                "Course Assignment Date",
                "Course Completion Date",
                "Course Type",
                "Self-Learning Completion",
                "Project Status",
                "OSL Score",
                "LVC Score",
                "Certification Status",
                "Self Learning Time",
                "Overall course completion score",
                "Labs Score",
                "Project",
                "Company",
                "Email sent",
                "Email sent at"
            ];
            if (type === 'progress') {
                columns = [
                    "Username",
                    "Course Name",
                    "Activity Level",
                    "Certification Status",
                    "Self-Learning Completion",
                    "Live Class Attended",
                    "Project Status",
                    "Overall course completion score",
                    "Project",
                    "Company",
                    "Email sent",
                    "Email sent at"
                ]
            } else if (type === 'overview') {
                columns = [
                    "Username",
                    "Course Name",
                    "Activity Level",
                    "Certification Status",
                    "Course Assignment Date",
                    "Course Completion Date",
                    "Project",
                    "Company",
                    "Email sent",
                    "Email sent at"
                ]
            }
            const pdf = new jsPDF("p", "pt", "a3");
            const rows = [];
            let list = (type === 'analytics')
                ? projectsReportData
                : (type === 'overview' ? overViewProjectsReportData : progressProjectsReportData);
            list = list || [];
            if (!list.length) {
                notify('error', "Report data not found!");
                return;
            }
            for (let i = 0; i < list.length; i++) {
                let temp = [];
                if (type === 'progress') {
                    temp = [
                        list[i].Username || 'N/A',
                        list[i].course_title || list[i].course || 'N/A',
                        list[i].activity_level || 'N/A',
                        list[i].certification_status || 'N/A',
                        list[i].self_learning_completion || '0',
                        list[i].live_class_attended || 'N/A',
                        list[i].project_result || 'N/A',
                        list[i].test_score || '0',
                        formatProjectLabel(list[i]),
                        formatCompanyLabel(list[i]),
                        formatTalentAccessEmail(list[i]),
                        formatEmailSentAt(list[i].email_sent_at)
                    ];
                } else if (type === 'overview') {
                    temp = [
                        list[i].Username || 'N/A',
                        list[i].course_title || list[i].course || 'N/A',
                        list[i].activity_level || 'N/A',
                        list[i].certification_status || 'N/A',
                        list[i].course_assignment_date || 'N/A',
                        list[i].course_completion_date || 'N/A',
                        formatProjectLabel(list[i]),
                        formatCompanyLabel(list[i]),
                        formatTalentAccessEmail(list[i]),
                        formatEmailSentAt(list[i].email_sent_at)
                    ];
                } else {
                    temp = [
                        list[i].Username || 'N/A',
                        list[i]['Learner Email'] || 'N/A',
                        list[i].order_type || 'N/A',
                        list[i].course_title || list[i].course || 'N/A',
                        list[i].course_assignment_date || 'N/A',
                        list[i]?.course_completion_date || 'N/A',
                        list[i]?.course_type || 'N/A',
                        list[i]?.self_learning_completion || '0',
                        list[i]?.project_result || 'N/A',
                        list[i]?.osl_score || '0',
                        list[i]?.lvc_sore || '0',
                        list[i]?.certification_status || 'N/A',
                        list[i].self_learning_time || 'N/A',
                        list[i]['Assessment Test'] || '0',
                        list[i]?.labs_score ?? 'N/A',
                        formatProjectLabel(list[i]),
                        formatCompanyLabel(list[i]),
                        formatTalentAccessEmail(list[i]),
                        formatEmailSentAt(list[i].email_sent_at)
                    ];
                }
                rows.push(temp);
            }
            pdf.autoTable(columns, rows, {
                startY: 65,
                theme: "grid",
                styles: {
                    font: "times",
                    halign: "center",
                    cellPadding: 2,
                    lineWidth: 0.5,
                    lineColor: [0, 0, 0],
                    textColor: [0, 0, 0]
                },
                headStyles: {
                    textColor: [0, 0, 0],
                    fontStyle: "normal",
                    lineWidth: 0.5,
                    lineColor: [0, 0, 0],
                    fillColor: [166, 204, 247]
                },
                alternateRowStyles: {
                    fillColor: [212, 212, 212],
                    textColor: [0, 0, 0],
                    lineWidth: 0.5,
                    lineColor: [0, 0, 0]
                },
                rowStyles: {
                    lineWidth: 0.5,
                    lineColor: [0, 0, 0]
                },
                tableLineColor: [0, 0, 0]
            });
            pdf.save(filename);
        } finally {
            setPrintReportLoading({ ...printReportLoading, [type]: false });
        }
    }

    const analyticsDisplayState = getFilterStateForTab('analytics');
    const overviewDisplayState = getFilterStateForTab('overview');
    const progressDisplayState = getFilterStateForTab('progress');
    const paginationProps = (tabKey) => ({
        pagination: paginationByTab[tabKey],
        pendingPage: pendingPageByTab[tabKey],
        handlePageSizeChange,
        handleRetry: () => handleRetry(tabKey),
    });

    return (
        <Layout>
            {initialLoading ? (
                <RouteSkeleton variant="report-table" />
            ) : (
            <main className={classes.page}>
                <div className={classes.pageHeader}>
                    <Typography className={classes.pageEyebrow}>Reporting</Typography>
                    <Typography className={classes.pageTitle}>Learning Service</Typography>
                    <Typography className={classes.pageDescription}>
                        Explore detailed analytics, overview, and progress reports for learning activity.
                    </Typography>
                </div>
                <div className={classes.panel}>
                    <CustomTabs
                        activeIndex={activeIndex}
                        handleChangeActiveNotiTab={handleTabChange}
                        disableSwipe
                        lazyMount
                        tabs={[
                            {
                                label: 'Detailed analytics report',
                                children: (
                                    <DetailsAnalyticsTab
                                        type="analytics"
                                        title="Detailed Analytics"
                                        filters={filters}
                                        pageName='learning'
                                        allFilterState={analyticsDisplayState}
                                        handleChange={handleChange}
                                        columns={withAttributionColumns(analyticsColumns)}
                                        loading={loadingByTab.analytics}
                                        handleItemClick={handleItemClick}
                                        list={projectsReportData}
                                        label="Detailed analytics report"
                                        handlePageChange={(evt, page) => handlePageChange(evt, page, 'analytics')}
                                        handleSubmitFilter={handleSubmitFilter}
                                        handleClearFilters={handleClearFilters}
                                        handleGoToPage={handleGoToPage}
                                        handleDownload={() => handleDownload('analytics')}
                                        handlePrint={() => handlePrint('analytics')}
                                        downloadReportLoading={downloadReportLoading}
                                        printReportLoading={printReportLoading}
                                        appliedPeriodText={formatAppliedPeriod(filters?.date_range)}
                                        filterError={errorByTab.analytics}
                                        showNotStartedCount={false}
                                        {...paginationProps('analytics')}
                                    />
                                )
                            },
                            {
                                label: 'Overview report',
                                children: (
                                    <DetailsAnalyticsTab
                                        type="overview"
                                        title="Overview Report"
                                        filters={overViewFilters}
                                        pageName='learning'
                                        allFilterState={overviewDisplayState}
                                        handleChange={handleChange}
                                        columns={withAttributionColumns(overViewColumn)}
                                        loading={loadingByTab.overview}
                                        handleItemClick={handleItemClick}
                                        list={overViewProjectsReportData}
                                        label="Overview report"
                                        handlePageChange={handleOverViewPageChange}
                                        handleSubmitFilter={handleSubmitFilter}
                                        handleClearFilters={handleClearFilters}
                                        handleGoToPage={handleGoToPage}
                                        downloadReportLoading={downloadReportLoading}
                                        handlePrint={() => handlePrint('overview')}
                                        handleDownload={() => handleDownload('overview')}
                                        printReportLoading={printReportLoading}
                                        appliedPeriodText={formatAppliedPeriod(overViewFilters?.date_range)}
                                        filterError={errorByTab.overview}
                                        showNotStartedCount={false}
                                        {...paginationProps('overview')}
                                    />
                                )
                            },
                            {
                                label: 'Progress report',
                                children: (
                                    <DetailsAnalyticsTab
                                        type="progress"
                                        title="Progress Report"
                                        filters={progressFilters}
                                        pageName='learning'
                                        allFilterState={progressDisplayState}
                                        handleChange={handleChange}
                                        columns={withAttributionColumns(progressColumn)}
                                        loading={loadingByTab.progress}
                                        handleItemClick={handleItemClick}
                                        list={progressProjectsReportData}
                                        label="Progress report"
                                        handlePageChange={handleProgressPageChange}
                                        handleSubmitFilter={handleSubmitFilter}
                                        handleClearFilters={handleClearFilters}
                                        handleGoToPage={handleGoToPage}
                                        downloadReportLoading={downloadReportLoading}
                                        handleDownload={() => handleDownload('progress')}
                                        handlePrint={() => handlePrint('progress')}
                                        printReportLoading={printReportLoading}
                                        appliedPeriodText={formatAppliedPeriod(progressFilters?.date_range)}
                                        filterError={errorByTab.progress}
                                        showNotStartedCount={false}
                                        {...paginationProps('progress')}
                                    />
                                )
                            }
                        ]}
                    />
                </div>
            </main>
            )}
        </Layout>
    );
}

export default Report;
